import React, { useEffect, useMemo, useState } from 'react';
import CsDiagnostics from '../../models/CsDiagnostics';
import Multiselect from 'multiselect-react-dropdown';
import { toast } from 'react-toastify';
import { fetchChargingStations } from '../../api/csDiagnosticsApi';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker } from '@mui/x-date-pickers/MobileDateTimePicker';
import { renderTimeViewClock } from '@mui/x-date-pickers/timeViewRenderers';
import dayjs from 'dayjs';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const pickerTheme = createTheme({
  palette: {
    primary: {
      main: '#6200ee',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiPickersLayout: {
      styleOverrides: {
        root: {
          '.MuiPickersLayout-contentWrapper': {
            borderRadius: '12px',
          },
        },
      },
    },
    MuiDateTimePickerToolbar: {
      styleOverrides: {
        root: {
          padding: '16px',
        },
        ampmSelection: {
          flexDirection: 'column',
          gap: '4px',
          '.MuiTypography-root': {
            padding: '4px 8px',
            borderRadius: '4px',
            '&.Mui-selected': {
              backgroundColor: 'rgba(98, 0, 238, 0.12)'
            },
          },
        },
      },
    },
    MuiClock: {
      styleOverrides: {
        root: {
          backgroundColor: '#f0f0f0',
          padding: '2.7rem 1rem'
        },
        clock: {
          backgroundColor: '#e0e0e0',
        },
      },
    },
  },
});

const formatPickerValue = (value) => value.format('YYYY-MM-DDTHH:mm:ss');

const clampDateTime = (newValue, maxDateTime) => {
  if (!newValue || !newValue.isValid()) return null;
  return newValue.isAfter(maxDateTime) ? maxDateTime : newValue;
};

const getNow = () => dayjs();

const getStartMaxDateTime = (endDate) => {
  const now = getNow();
  if (!endDate) return now;
  const end = dayjs(endDate);
  return end.isValid() && end.isBefore(now) ? end : now;
};

const FormPartial = (props) => {
  const { csDiagnostics, setCsDiagnostics, setIsLoading } = props;
  const [chargingStations, setChargingStations] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoading(true);
        const data = await fetchChargingStations();
        if (cancelled) return;
        if (data.length > 0) {
          setChargingStations(data);
        } else {
          toast.warning('Charging stations are null or not found.');
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.response?.data?.message || error?.message || 'Something went wrong !');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [setIsLoading]);

  useEffect(() => {
    if (!csDiagnostics.end_date) {
      const now = dayjs().format('YYYY-MM-DDTHH:mm:ss');
      changeValue('end_date', now);
    }
  }, []);

  const mergeDiagnostics = (mutator) => {
    setCsDiagnostics((prev) => {
      const m = new CsDiagnostics(prev);
      mutator(m);
      return m.toStateJson();
    });
  };

  const onChargingStationSelect = (_selectedList, selectedItem) => {
    mergeDiagnostics((m) => {
      m.charging_station.push(selectedItem);
      m.validate('charging_station');
    });
  };

  const onChargingStationRemove = () => {
    mergeDiagnostics((m) => {
      m.charging_station = [];
      m.validate('charging_station');
    });
  };

  const validateValue = (field, model) => {
    if (Object.keys(model.errors).length === 0) return; // Don't validate if no errors yet (pre-search)
    if (field === 'start_date') {
      model.validate('end_date');
    } else if (field === 'end_date') {
      model.validate('start_date');
    }
    model.validate(field);
  };

  const changeValue = (field, value) => {
    mergeDiagnostics((m) => {
      m[field] = value;
      validateValue(field, m);
    });
  };

  const modelForDisplay = useMemo(() => new CsDiagnostics(csDiagnostics), [csDiagnostics]);

  const selectedChargingStation = useMemo(() => {
    const id = csDiagnostics.charging_station?.[0]?.device_id;
    if (!id || !chargingStations.length) return [];
    return chargingStations.filter((cs) => cs.device_id === id);
  }, [csDiagnostics.charging_station, chargingStations]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <ThemeProvider theme={pickerTheme}>
        <div className="cs-diagnostics-toolbar__field cs-diagnostics-toolbar__field--station">
          <label htmlFor="charging_station" className="form-label mb-1">Select Charging Station</label>
          <div className="cs-multiselect-wrapper" style={{border: '1px solid lightGrey', borderRadius: '5px'}}>
            <Multiselect
              id="charging_station"
              options={chargingStations}
              selectedValues={selectedChargingStation}
              displayValue="device_id"
              avoidHighlightFirstOption
              onSelect={onChargingStationSelect}
              onRemove={onChargingStationRemove}
              customArrow
              placeholder="Select Charging Station"
              selectionLimit={1}
            />
          </div>
          <div className="cs-error-container">
            {modelForDisplay.errors.charging_station && (
              <div className="form-text text-danger mt-0">{modelForDisplay.errors.charging_station}</div>
            )}
          </div>
        </div>
        <div className="cs-diagnostics-toolbar__field cs-mui-picker">
          <label htmlFor="start_date" className="form-label mb-1">Select Start Date and Time</label>
          <MobileDateTimePicker
            value={modelForDisplay.start_date ? dayjs(modelForDisplay.start_date) : null}
            maxDateTime={getStartMaxDateTime(modelForDisplay.end_date)}
            onChange={(newValue) => {
              const clamped = clampDateTime(newValue, getStartMaxDateTime(modelForDisplay.end_date));
              if (clamped) {
                changeValue('start_date', formatPickerValue(clamped));
              }
            }}
            format="DD/MM/YYYY hh:mm A"
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            ampm={true}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: !!modelForDisplay.errors.start_date,
                helperText: modelForDisplay.errors.start_date,
                className: 'cs-mui-input'
              },
            }}
          />
        </div>
        <div className="cs-diagnostics-toolbar__field cs-mui-picker">
          <label htmlFor="end_date" className="form-label mb-1">Select End Date and Time</label>
          <MobileDateTimePicker
            value={modelForDisplay.end_date ? dayjs(modelForDisplay.end_date) : null}
            maxDateTime={getNow()}
            minDateTime={modelForDisplay.start_date ? dayjs(modelForDisplay.start_date) : undefined}
            onChange={(newValue) => {
              const clamped = clampDateTime(newValue, getNow());
              if (clamped) {
                changeValue('end_date', formatPickerValue(clamped));
              }
            }}
            format="DD/MM/YYYY hh:mm A"
            viewRenderers={{
              hours: renderTimeViewClock,
              minutes: renderTimeViewClock,
              seconds: renderTimeViewClock,
            }}
            ampm={true}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: !!modelForDisplay.errors.end_date,
                helperText: modelForDisplay.errors.end_date,
                className: 'cs-mui-input'
              },
            }}
          />
        </div>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default FormPartial;
